import { create } from 'zustand'
import { supabase } from './supabase'
import type { Profile, Product, Notification } from '@/types/database'

interface CartItem {
  product: Product
  quantity: number
  negotiatedPrice?: number
  selectedColor?: string
  selectedSize?: string
}

interface CartState {
  items: CartItem[]
  addItem: (product: Product) => void
  removeItem: (productId: string) => void
  updateQuantity: (productId: string, quantity: number) => void
  updateNegotiatedPrice: (productId: string, price: number) => void
  clearCart: () => void
  total: () => number
}

interface AuthState {
  user: Profile | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, role: Profile['role']) => Promise<void>
  signOut: () => Promise<void>
  loadUser: () => Promise<void>
  updateProfile: (data: Partial<Profile>) => Promise<void>
}

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  error: string | null
  loadNotifications: () => Promise<void>
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  hasNewNotification: boolean
  setHasNewNotification: (value: boolean) => void
}

interface AdminState {
  sellerApplications: any[]
  deliveryApplications: any[]
  loading: boolean
  error: string | null
  fetchSellerApplications: () => Promise<void>
  fetchDeliveryApplications: () => Promise<void>
  updateSellerApplication: (id: string, status: string, reason?: string) => Promise<void>
  updateDeliveryApplication: (id: string, status: string, reason?: string) => Promise<void>
  analytics: {
    totalSales: number
    totalRevenue: number
    activeUsers: number
    pendingApplications: number
  }
  fetchAnalytics: () => Promise<void>
}

export const useCartStore = create<CartState>((set, get) => ({
  items: [],
  addItem: (product) => {
    set((state) => {
      const existingItem = state.items.find(
        (item) => item.product.id === product.id
      )
      
      // Make sure we preserve the selected color and size when adding to cart
      const selectedColor = product.selectedColor || null
      const selectedSize = product.selectedSize || null
      
      if (existingItem) {
        return {
          items: state.items.map((item) =>
            item.product.id === product.id
              ? { 
                  ...item, 
                  quantity: item.quantity + 1,
                  selectedColor: selectedColor || item.selectedColor,
                  selectedSize: selectedSize || item.selectedSize
                }
              : item
          ),
        }
      }
      return { 
        items: [...state.items, { 
          product, 
          quantity: 1,
          selectedColor,
          selectedSize 
        }] 
      }
    })
  },
  removeItem: (productId) => {
    set((state) => ({
      items: state.items.filter((item) => item.product.id !== productId),
    }))
  },
  updateQuantity: (productId, quantity) => {
    set((state) => ({
      items: state.items.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      ),
    }))
  },
  updateNegotiatedPrice: (productId, price) => {
    set((state) => ({
      items: state.items.map((item) =>
        item.product.id === productId
          ? { ...item, negotiatedPrice: price }
          : item
      ),
    }))
  },
  clearCart: () => set({ items: [] }),
  total: () => {
    const items = get().items
    return items.reduce((total, item) => {
      const price = item.negotiatedPrice || item.product.price
      return total + price * item.quantity
    }, 0)
  },
}))

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  signIn: async (email, password) => {
    try {
      console.log('Attempting to sign in with email:', email);
      set({ loading: true })
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error
      
      // Refresh session after sign in
      console.log('Sign in successful, refreshing session');
      const { data: { session } } = await supabase.auth.refreshSession()
      const { data: { user: authUser } } = await supabase.auth.getUser()
      
      console.log('Session refresh result:', { session: !!session, authUser: !!authUser });
      
      if (authUser && session) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authUser.id)
          .maybeSingle()
        
        if (profileError) throw profileError
        
        if (!profile) {
          console.log('No profile found, creating new profile');
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert([{ 
              id: authUser.id,
              role: authUser.user_metadata?.role || 'buyer',
              email: authUser.email
            }])
            .select()
            .maybeSingle()
            
          if (createError) throw createError
          console.log('New profile created:', newProfile);
          set({ user: newProfile, loading: false })
        } else {
          console.log('Profile loaded:', profile);
          set({ user: profile, loading: false })
        }
      } else {
        console.log('No session or user after sign in');
        set({ user: null, loading: false })
      }
    } catch (error) {
      console.error('Sign in error:', error);
      set({ user: null, loading: false })
      throw error
    }
  },
  signUp: async (email, password, role) => {
    try {
      console.log('Attempting to sign up with email:', email, 'and role:', role);
      set({ loading: true })
      const { error: signUpError, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            role
          }
        }
      })
      if (signUpError) throw signUpError

      console.log('Sign up response:', data);
      if (data.user) {
        const { error: profileError, data: profile } = await supabase
          .from('profiles')
          .insert([{ 
            id: data.user.id,
            role: data.user.user_metadata?.role || 'buyer',
            email: data.user.email
          }])
          .select()
          .maybeSingle()
        
        if (profileError) throw profileError
        console.log('Profile created:', profile);
        set({ user: profile, loading: false })
      } else {
        console.log('No user data after sign up');
        set({ user: null, loading: false })
      }
    } catch (error) {
      console.error('Sign up error:', error);
      set({ user: null, loading: false })
      throw error
    }
  },
  signOut: async () => {
    try {
      console.log('Signing out');
      set({ loading: true })
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      
      // Clear user state and local storage
      localStorage.removeItem('app-session')
      set({ user: null, loading: false })
    } catch (error) {
      console.error('Error signing out:', error)
      // Still clear user state even if there's an error
      localStorage.removeItem('app-session')
      set({ user: null, loading: false })
    }
  },
  loadUser: async () => {
    try {
      console.log('Loading user data');
      set({ loading: true })
      
      // First check and refresh session if needed
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      console.log('Session check result:', { session: !!session, error: sessionError });
      
      if (sessionError) throw sessionError
      
      if (!session) {
        console.log('No session found, user not authenticated');
        set({ user: null, loading: false })
        return
      }
      
      // Try to refresh the session
      console.log('Attempting to refresh session');
      const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession()
      
      console.log('Session refresh result:', { refreshedSession: !!refreshedSession, error: refreshError });
      if (refreshError) {
        // If refresh fails, sign out and clear state
        console.error('Session refresh failed, signing out:', refreshError);
        await supabase.auth.signOut()
        set({ user: null, loading: false })
        return
      }
      
      const { data: { user: authUser } } = await supabase.auth.getUser()
      console.log('Auth user data:', { authUser: !!authUser, id: authUser?.id });
      
      if (authUser && refreshedSession) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authUser.id)
          .maybeSingle()

        console.log('Profile fetch result:', { profile: !!profile, error: profileError });
        if (profileError) {
          console.error('Error fetching profile:', profileError);
          throw profileError;
        }
        
        if (!profile) {
          console.log('Profile not found, creating new profile');
          const { data: newProfile, error: createError } = await supabase
            .from('profiles')
            .insert([{ 
              id: authUser.id,
              role: authUser.user_metadata?.role || 'buyer',
              email: authUser.email
            }])
            .select()
            .maybeSingle()

          if (createError) throw createError
          console.log('New profile created:', newProfile);
          set({ user: newProfile, loading: false })
        } else {
          console.log('Existing profile loaded:', profile);
          set({ user: profile, loading: false })
        }
      } else {
        console.log('No authenticated user found');
        set({ user: null, loading: false })
      }
    } catch (error) {
      console.error('Error loading user:', error)
      // Clear state on error
      localStorage.removeItem('app-session')
      set({ user: null, loading: false })
    }
  },
  updateProfile: async (data) => {
    try {
      set({ loading: true })
      
      // Refresh session before updating profile
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession()
      if (refreshError) throw refreshError
      
      if (!session) {
        throw new Error('No active session')
      }
      
      const { error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', session.user.id)
        
      if (error) throw error
      
      // Reload user data
      const { data: updatedProfile, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .maybeSingle()
        
      if (fetchError) throw fetchError
      
      if (!updatedProfile) {
        throw new Error('Profile not found after update')
      }
      
      set({ user: updatedProfile, loading: false })
    } catch (error) {
      console.error('Error updating profile:', error)
      set({ loading: false })
      throw error
    }
  }
}))

const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  hasNewNotification: false,
  setHasNewNotification: (value: boolean) => set({ hasNewNotification: value }),
  loadNotifications: async () => {
    try {
      set({ loading: true, error: null })
      
      // Refresh session before loading notifications
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError
      
      if (!session) {
        throw new Error('No active session')
      }
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error
      
      const unreadCount = (data || []).filter(n => !n.read).length
      set({ notifications: data || [], unreadCount, error: null })
    } catch (err) {
      set({ error: 'Failed to load notifications' })
    } finally {
      set({ loading: false })
    }
  },
  markAsRead: async (notificationId) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('No active session')
      
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)

      if (error) throw error
      
      set((state) => ({
        notifications: state.notifications.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        ),
        unreadCount: state.unreadCount - 1
      }))
    } catch (err) {
      console.error('Failed to mark notification as read:', err)
    }
  },
  markAllAsRead: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('No active session')
      
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', session.user.id)
        .eq('read', false)

      if (error) throw error
      
      set((state) => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0,
        hasNewNotification: false
      }))
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err)
    }
  }
}))

const useAdminStore = create<AdminState>((set, get) => ({
  sellerApplications: [],
  deliveryApplications: [],
  loading: false,
  error: null,
  analytics: {
    totalSales: 0,
    totalRevenue: 0,
    activeUsers: 0,
    pendingApplications: 0
  },
  fetchSellerApplications: async () => {
    try {
      set({ loading: true, error: null })
      
      // Refresh session before fetching
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError
      
      if (!session) {
        throw new Error('No active session')
      }
      
      const { data, error } = await supabase
        .from('seller_stores')
        .select(`
          *,
          profiles:seller_id (
            id,
            full_name,
            email,
            phone_number,
            avatar_url
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      set({ sellerApplications: data || [] })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch seller applications'
      set({ error: errorMessage })
      console.error('Error:', err)
    } finally {
      set({ loading: false })
    }
  },
  fetchDeliveryApplications: async () => {
    try {
      set({ loading: true, error: null })
      
      // Refresh session before fetching
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError
      
      if (!session) {
        throw new Error('No active session')
      }
      
      const { data, error } = await supabase
        .from('delivery_partners')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      set({ deliveryApplications: data || [] })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch delivery applications'
      set({ error: errorMessage })
      console.error('Error:', err)
    } finally {
      set({ loading: false })
    }
  },
  updateSellerApplication: async (id: string, status: string, reason?: string) => {
    try {
      set({ loading: true, error: null })
      
      // Refresh session before updating
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError
      
      if (!session) {
        throw new Error('No active session')
      }
      
      const { error } = await supabase
        .from('seller_stores')
        .update({ 
          status,
          ...(reason && { rejection_reason: reason })
        })
        .eq('id', id)

      if (error) throw error
      
      const store = get().sellerApplications.find(s => s.id === id)
      if (store) {
        await supabase
          .from('profiles')
          .update({ 
            status: status === 'approved' ? 'active' : status === 'rejected' ? 'blocked' : status,
            role: status === 'approved' ? 'seller' : 'buyer'
          })
          .eq('id', store.seller_id)

        await supabase
          .from('notifications')
          .insert([{
            user_id: store.seller_id,
            title: `Seller Application ${status.charAt(0).toUpperCase() + status.slice(1)}`,
            content: status === 'approved' 
              ? 'Your seller application has been approved! You can now start selling.'
              : `Your seller application has been ${status}${reason ? `. Reason: ${reason}` : ''}`,
            type: 'application'
          }])
      }

      await get().fetchSellerApplications()
      await get().fetchAnalytics()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update seller application'
      set({ error: errorMessage })
      console.error('Error:', err)
    } finally {
      set({ loading: false })
    }
  },
  updateDeliveryApplication: async (id: string, status: string, reason?: string) => {
    try {
      set({ loading: true, error: null })
      
      // Refresh session before updating
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError
      
      if (!session) {
        throw new Error('No active session')
      }
      
      const { error } = await supabase
        .from('delivery_partners')
        .update({ 
          status,
          ...(reason && { rejection_reason: reason })
        })
        .eq('id', id)

      if (error) throw error

      await supabase
        .from('profiles')
        .update({ 
          status: status === 'active' ? 'active' : status === 'rejected' ? 'blocked' : status,
          role: status === 'active' ? 'delivery' : 'buyer'
        })
        .eq('id', id)

      await supabase
        .from('notifications')
        .insert([{
          user_id: id,
          title: `Delivery Partner Application ${status.charAt(0).toUpperCase() + status.slice(1)}`,
          content: status === 'active' 
            ? 'Your delivery partner application has been approved! You can now start delivering.'
            : `Your delivery partner application has been ${status}${reason ? `. Reason: ${reason}` : ''}`,
          type: 'application'
        }])
      
      await get().fetchDeliveryApplications()
      await get().fetchAnalytics()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update delivery application'
      set({ error: errorMessage })
      console.error('Error:', err)
    } finally {
      set({ loading: false })
    }
  },
  fetchAnalytics: async () => {
    try {
      set({ loading: true, error: null })
      
      // Refresh session before fetching
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError) throw sessionError
      
      if (!session) {
        throw new Error('No active session')
      }

      const { data: salesData } = await supabase
        .from('orders')
        .select('final_price')
        .eq('status', 'delivered')

      const { count: activeUsers } = await supabase
        .from('profiles')
        .select('id', { count: 'exact' })
        .eq('status', 'active')

      const { count: pendingSellers } = await supabase
        .from('seller_stores')
        .select('id', { count: 'exact' })
        .eq('status', 'pending')

      const { count: pendingDelivery } = await supabase
        .from('delivery_partners')
        .select('id', { count: 'exact' })
        .eq('status', 'pending')

      set({
        analytics: {
          totalSales: salesData?.length || 0,
          totalRevenue: salesData?.reduce((sum, order) => sum + order.final_price, 0) || 0,
          activeUsers: activeUsers || 0,
          pendingApplications: (pendingSellers || 0) + (pendingDelivery || 0)
        }
      })
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch analytics'
      set({ error: errorMessage })
      console.error('Error fetching analytics:', err)
    } finally {
      set({ loading: false })
    }
  }
}))